import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { translations, DEFAULT_LANGUAGE, AVAILABLE_LANGUAGES } from "./translations";

const LanguageContext = createContext(null);
const STORAGE_KEY = "fp_lang";
const PICKED_KEY = "fp_lang_picked";

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_LANGUAGE;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && AVAILABLE_LANGUAGES.includes(stored)) return stored;
    return DEFAULT_LANGUAGE;
  });
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, lang);
    if (typeof document !== "undefined") document.documentElement.lang = lang === "fr" ? "fr-CA" : "en-US";
  }, [lang]);

  useEffect(() => {
    const picked = localStorage.getItem(PICKED_KEY);
    if (!picked) setShowPicker(true);
  }, []);

  const chooseLanguage = useCallback((next) => {
    if (AVAILABLE_LANGUAGES.includes(next)) setLang(next);
    localStorage.setItem(PICKED_KEY, "1");
    setShowPicker(false);
  }, []);

  const toggle = useCallback(() => {
    setLang((prev) => (prev === "en" ? "fr" : "en"));
  }, []);

  const openPicker = useCallback(() => setShowPicker(true), []);
  const closePicker = useCallback(() => {
    localStorage.setItem(PICKED_KEY, "1");
    setShowPicker(false);
  }, []);

  const dict = translations[lang] || translations[DEFAULT_LANGUAGE];

  const t = useCallback(
    (path, fallback = "") => {
      const parts = path.split(".");
      let node = dict;
      for (const p of parts) {
        if (node == null) return fallback;
        node = node[p];
      }
      return node == null ? fallback : node;
    },
    [dict]
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang, toggle, t, dict, showPicker, openPicker, closePicker, chooseLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
};
